import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Loading, Modal } from 'react-daisyui';
import { AxiosService } from 'src/axios/axios.service';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useSubscriptionManagerContext } from '../../SubscriptionManager.context';

function PaymentCompletedModal(): React.ReactElement {
  const { t } = useTranslation('settings/SubscriptionManager');
  const { showPaymentCompletedModal, togglePaymentCompletedModal } =
    useSubscriptionManagerContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { getSummary } = useSubscriptionContext();
  const { refetch: refetchFeatureFlags } = useFeatureFlagsContext();
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  // Reconcile post-upgrade state BEFORE dismissing the modal so the user can't
  // out-race the reconciliation by navigating to /leadbot during the in-flight
  // refetches.
  //
  // Order matters:
  // 1. sync-access first — writes workspace_plan + onboarding_completed_at on
  //    workspace-service. /feature-flags derives from these (PR #396), so a
  //    parallel feature-flags refetch would re-cache stale `false` values if
  //    it raced ahead of the write.
  // 2. Then refetch SubscriptionProvider + FeatureFlagsProvider in parallel
  //    so React state reflects the just-written backend state.
  // 3. Only then close the modal.
  //
  // Every step's failure is non-fatal — /leadbot's own mount re-runs
  // sync-access defensively, and the providers fall back to stale state
  // rather than blocking. The button's loading state gives the user
  // unambiguous feedback during the ~800ms-1.5s reconciliation window.
  async function handleAcknowledge() {
    if (!currentWorkspace?.id) {
      togglePaymentCompletedModal(false);
      return;
    }
    setIsAcknowledging(true);
    try {
      await AxiosService.post(
        `workspace/${currentWorkspace.id}/lead-agent/sync-access`,
        {},
      );
    } catch {
      // Non-fatal — fall through to the refetches anyway. The providers'
      // socket-driven refetch path remains as a backstop.
    }
    try {
      await Promise.all([getSummary(), refetchFeatureFlags()]);
    } catch {
      // Refetch failures shouldn't trap the user inside the modal. Worst
      // case they see slightly stale state and a manual refresh fixes it.
    }
    setIsAcknowledging(false);
    togglePaymentCompletedModal(false);
  }

  return (
    <Modal
      open={showPaymentCompletedModal}
      className="PaymentCompletedModal"
      backdrop
    >
      <Modal.Body>
        <p className="PaymentCompletedModal__emoji">🎉</p>
        <p className="PaymentCompletedModal__message">
          {t('page.PaymentCompletedModal.successfulPayment')}
        </p>
        <ul className="PaymentCompletedModal__list">
          <li className="PaymentCompletedModal__listItem">
            <Trans
              i18nKey="settings/SubscriptionManager:page.PaymentCompletedModal.renovation"
              components={{
                span: <span className="PaymentCompletedModal__highlight" />,
              }}
            />
          </li>
          <li className="PaymentCompletedModal__listItem">
            <Trans
              i18nKey="settings/SubscriptionManager:page.PaymentCompletedModal.setup"
              components={{
                span: <span className="PaymentCompletedModal__highlight" />,
              }}
            />
          </li>
          <li className="PaymentCompletedModal__listItem">
            <Trans
              i18nKey="settings/SubscriptionManager:page.PaymentCompletedModal.support"
              components={{
                span: <span className="PaymentCompletedModal__highlight" />,
              }}
            />
          </li>
        </ul>
      </Modal.Body>
      <Modal.Actions>
        <Button
          color="primary"
          onClick={handleAcknowledge}
          disabled={isAcknowledging}
          fullWidth
          data-testid="start-with-leadsales-button"
        >
          {isAcknowledging ? (
            <Loading size="sm" />
          ) : (
            t('page.PaymentCompletedModal.startButton')
          )}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default PaymentCompletedModal;
